import { useEffect } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { movieCategories } from "@/mocks/categories";
import { newsList } from "@/mocks/news";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import MovieCard from "@/components/base/MovieCard";
import NewsListItem from "@/components/base/NewsListItem";
import SectionHeading from "@/components/base/SectionHeading";

export default function Home() {
  useEffect(() => {
    document.title = "泡泡影视 - 最新电影电视剧资源分享与影视资讯";
  }, []);

  const featured = movies.filter((movie) => movie.featured).slice(0, 5);
  const latest = [...movies].sort((a, b) => b.year - a.year).slice(0, 8);
  const hotNews = newsList.slice(0, 4);

  return (
    <div className="w-full">
      <div className="w-full px-0 md:px-8 pt-4 md:pt-6">
        <HeroCarousel movies={featured} />
      </div>

      <section className="w-full px-4 md:px-8 pt-12">
        <SectionHeading
          title="影视分类"
          subtitle="按类型快速找到你想看的内容"
          icon="ri-layout-grid-line"
          action={
            <Link
              to="/category"
              className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              全部分类
              <i className="ri-arrow-right-s-line text-lg" />
            </Link>
          }
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {movieCategories.map((cat) => (
            <Link
              key={cat.key}
              to={`/category?cat=${encodeURIComponent(cat.key)}`}
              className="group relative h-40 md:h-48 rounded-lg overflow-hidden bg-foreground-950 cursor-pointer"
            >
              <img
                src={cat.image}
                alt={`${cat.name}分类`}
                title={`${cat.name} 影视资源分类`}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold text-background-50">{cat.name}</h3>
                  <span className="rounded-full bg-accent-500 px-2 py-0.5 text-xs font-medium text-primary-950">
                    {cat.count}
                  </span>
                </div>
                <p className="mt-1 text-xs text-background-300">{cat.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pt-14">
        <SectionHeading
          title="最新影视资源"
          subtitle="按最新发布排序，实时更新"
          icon="ri-film-line"
          action={
            <Link
              to="/category"
              className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看更多
              <i className="ri-arrow-right-s-line text-lg" />
            </Link>
          }
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {latest.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pt-14">
        <SectionHeading
          title="影视资讯精选"
          subtitle="行业动态、影评与票房解读"
          icon="ri-newspaper-line"
          action={
            <Link
              to="/news"
              className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              全部资讯
              <i className="ri-arrow-right-s-line text-lg" />
            </Link>
          }
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {hotNews.map((article) => (
            <NewsListItem key={article.id} article={article} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pt-14">
        <div className="rounded-xl bg-primary-950 px-6 py-10 md:px-12 md:py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
              关于泡泡影视
            </h2>
            <p className="mt-3 text-sm md:text-base text-background-300 leading-relaxed">
              泡泡影视是一个专注影视内容分享与行业资讯的内容平台。我们持续整理最新的电影、电视剧资源信息，并带来深度影评、票房解读与剧集情报，让你用更少的时间找到更想看的内容。
            </p>
            <div className="mt-5 flex flex-wrap gap-6 text-sm">
              <div>
                <div className="font-heading text-xl font-bold text-accent-400">2,900+</div>
                <div className="text-background-400">收录影视</div>
              </div>
              <div>
                <div className="font-heading text-xl font-bold text-accent-400">12</div>
                <div className="text-background-400">内容分类</div>
              </div>
              <div>
                <div className="font-heading text-xl font-bold text-accent-400">每日</div>
                <div className="text-background-400">资讯更新</div>
              </div>
            </div>
          </div>
          <Link
            to="/about"
            className="shrink-0 flex items-center gap-2 px-5 py-3 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            了解更多
            <i className="ri-arrow-right-line text-lg" />
          </Link>
        </div>
      </section>
    </div>
  );
}